import { userManager } from '../auth/oidcConfig';
import { config } from '../config';
import { FleetEvent } from '../types/events';

type MessageHandler = (event: FleetEvent) => void;

/** Path of the api-backend WebSocket gateway (native WebSocket, not socket.io). */
const WS_PATH = '/api/v1/ws';

/**
 * Client for the api-backend real-time gateway. Speaks the native WebSocket
 * protocol and parses the {type, data, timestamp} envelope into typed
 * FleetEvents. Handlers registered via onMessage receive every event; callers
 * discriminate on `event.type`.
 */
class WebSocketService {
  private socket: WebSocket | null = null;
  private messageHandlers: MessageHandler[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelayMs = 5000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByClient = false;
  private connectUrl?: string;

  async connect(url?: string): Promise<void> {
    this.closedByClient = false;
    this.connectUrl = url;

    const base = url || config.wsUrl;
    const target = new URL(WS_PATH, base);

    // Browsers cannot set an Authorization header on the WebSocket handshake,
    // so the bearer token is passed as a query parameter (the gateway accepts
    // access_token as well as the header).
    const user = await userManager.getUser();
    if (user?.id_token) {
      target.searchParams.set('access_token', user.id_token);
    }

    // A reconnect may have raced ahead while awaiting the token.
    if (this.closedByClient) {
      return;
    }

    const socket = new WebSocket(target.toString());
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectAttempts = 0;
    };

    socket.onmessage = (frame: MessageEvent) => {
      let event: FleetEvent;
      try {
        event = JSON.parse(frame.data as string) as FleetEvent;
      } catch {
        console.error('WebSocket: dropping malformed event frame');
        return;
      }
      this.messageHandlers.forEach((handler) => handler(event));
    };

    socket.onclose = () => {
      this.socket = null;
      if (!this.closedByClient) {
        this.scheduleReconnect();
      }
    };

    socket.onerror = () => {
      // close fires after error; reconnection is handled in onclose.
      socket.close();
    };
  }

  private scheduleReconnect(): void {
    if (
      this.reconnectTimer !== null ||
      this.reconnectAttempts >= this.maxReconnectAttempts
    ) {
      return;
    }
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect(this.connectUrl);
    }, this.reconnectDelayMs);
  }

  disconnect(): void {
    this.closedByClient = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.push(handler);
    // Return unsubscribe function
    return () => {
      this.messageHandlers = this.messageHandlers.filter((h) => h !== handler);
    };
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }
}

export const websocketService = new WebSocketService();
